// src/repositories/auth.repository.ts
import { safeRequest } from "../api/safeRequest.ts";
import { AuthNetwork } from "../api/auth.network.ts";
import { RegisterResponse, RegisterResponseSchema } from "../models/auth.model.ts";
import { UserData, UserDataSchema } from "../models/user.model.ts";
import { ServiceRepository } from "./service.repository.ts";
import { useServiceStore } from "../stores/service.store.ts";
import { useBookmarkStore } from "../stores/bookmark.store.ts";
import { authTokenStore } from "../stores/authToken.store.ts";
import { userStore } from "../stores/user.store.ts";
import { uploadProfileImage } from "../api/image.network.ts";
import { compressImage } from "../utils/imageCompress.ts";

const authKeyGuest = "guest";
const authKeyUser = (userUuid: string) => `user:${userUuid}`;

export const AuthRepository = {
  signIn: async (
    params: { email: string; password: string; fcmToken: string },
    opts?: { handleGlobally?: boolean }
  ): Promise<RegisterResponse> =>
    safeRequest(async () => {
      const res = await AuthNetwork.signIn(params);
      const parsed = RegisterResponseSchema.parse(res.data);

      // 이후 인증 필요 호출(fetchDashboard, syncBookmarks)이 새 토큰으로 가도록 먼저 저장
      authTokenStore.getState().setAccessToken(parsed.access_token);
      if (parsed.user) userStore.getState().setUser(parsed.user);

      const userUuid = (parsed as any)?.user?.uuid ?? (parsed as any)?.uuid;
      if (userUuid) {
        await ServiceRepository.fetchDashboard({ authKey: authKeyUser(userUuid), force: true });
      } else {
        await ServiceRepository.fetchDashboard({ authKey: "user", force: true });
      }

      // 로그인 후 북마크 동기화 (fire-and-forget)
      const { BookmarkRepository } = await import("./bookamrk.repository.ts");
      BookmarkRepository.syncBookmarks();

      return parsed;
    }, opts),

  signUp: async (payload: any): Promise<RegisterResponse> =>
    safeRequest(async () => {
      const res = await AuthNetwork.signUp(payload);
      const parsed = RegisterResponseSchema.parse(res.data);

      authTokenStore.getState().setAccessToken(parsed.access_token);
      if (parsed.user) userStore.getState().setUser(parsed.user);

      // GA4 신규 가입 계측 — 이 경로는 정의상 항상 가입이다(소셜과 달리 서버
      // 플래그가 필요 없다). 화면이 아니라 여기 두는 건 호출부가 늘어도 한 번만
      // 나가게 하려는 것이다.
      (window as any).gtag?.("event", "sign_up", { method: "email" });

      const userUuid = (parsed as any)?.user?.uuid ?? (parsed as any)?.uuid;
      await ServiceRepository.fetchDashboard({ authKey: userUuid ? authKeyUser(userUuid) : "user", force: true });

      // 회원가입 후 북마크 동기화 (fire-and-forget)
      const { BookmarkRepository } = await import("./bookamrk.repository.ts");
      BookmarkRepository.syncBookmarks();

      return parsed;
    }),

  signOut: async (): Promise<void> =>
    safeRequest(async () => {
      await AuthNetwork.signOut();

      authTokenStore.getState().clearTokens();
      userStore.getState().clearUser();
      useServiceStore.getState().clear();
      useBookmarkStore.getState().clear();

      await ServiceRepository.fetchDashboard({ authKey: authKeyGuest, force: true });
    }),

  // ── 이메일 중복 확인 (로컬에서 에러 핸들링) ──────────────────────────────────────
  validateEmail: async (email: string): Promise<{ available: boolean }> =>
    safeRequest(async () => {
      const res = await AuthNetwork.validationEmail(email);
      return res.data;
    }, { handleGlobally: false }),

  // ── 유저명 중복 확인 (로컬에서 에러 핸들링) ─────────────────────────────────────
  validateUsername: async (name: string): Promise<{ available: boolean }> =>
    safeRequest(async () => {
      const res = await AuthNetwork.validationUsername(name);
      return res.data;
    }, { handleGlobally: false }),

  // ── 프로필 필드 업데이트 ───────────────────────────────────────────────────────
  updateProfile: async (payload: Record<string, unknown>): Promise<UserData> =>
    safeRequest(async () => {
      const res = await AuthNetwork.updateUserInfo(payload);
      const data = (res.data as any)?.data ?? res.data;
      return UserDataSchema.parse(data);
    }),

  // ── 프로필 이미지 업로드 + 프로필 갱신 (압축 → 업로드 → PATCH) ─────────────────
  uploadAndUpdateProfileImage: async (file: File): Promise<UserData> =>
    safeRequest(async () => {
      const compressed = await compressImage(file);
      const { url } = await uploadProfileImage(compressed);
      if (!url) throw new Error('Image upload failed.');
      const res = await AuthNetwork.updateUserInfo({ image: url });
      const data = (res.data as any)?.data ?? res.data;
      return UserDataSchema.parse(data);
    }),

  // ── 계정 탈퇴 ─────────────────────────────────────────────────────────────────
  withdrawAccount: async (): Promise<void> =>
    safeRequest(async () => {
      await AuthNetwork.withdrawalAccount();
      authTokenStore.getState().clearTokens();
      userStore.getState().clearUser();
      useServiceStore.getState().clear();
    }),
};
